import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from "#environments/environment";
import { Injectable } from "@angular/core";
import { Downtime, IDowntime } from "#models/event/downtime.model";
import {
  Observable,
  shareReplay,
  forkJoin,
  catchError,
  of,
  throwError,
} from "rxjs";
import { map } from "rxjs";
import { ApplyJobTemplateResponse } from "helpers/interfaces/apply-job-template-response";
@Injectable()
export class DowntimeService {
  private downtimes$ = this.http
    .get<Array<IDowntime>>(`${environment.apiEndpoint}/downtimes`)
    .pipe(shareReplay(1));
  constructor(private http: HttpClient) {}

  getList(): Observable<Array<IDowntime>> {
    return this.downtimes$;
  }

  get(downtimeID?: number): Observable<Array<IDowntime> | IDowntime> {
    const downtime = downtimeID ? "/" + downtimeID : "";
    return this.http.get<Array<IDowntime> | IDowntime>(
      `${environment.apiEndpoint}/downtimes` + downtime,
    );
  }

  getById(downtimeID: number): Observable<IDowntime> {
    return this.http.get<IDowntime>(
      `${environment.apiEndpoint}/downtimeDiscreetItem/${downtimeID}`,
    );
  }
  post(payload: any): Observable<IDowntime> {
    return this.http.post<IDowntime>(
      `${environment.apiEndpoint}/downtimes`,
      payload,
    );
  }

  put(downtimeId: number, payload: any): Observable<IDowntime> {
    return this.http.put<IDowntime>(
      `${environment.apiEndpoint}/downtimes/${downtimeId}`,
      payload,
    );
  }
  getPhase3ById(downtimeId: string): Observable<IDowntime> {
    return this.http.put<IDowntime>(
      `${environment.apiEndpoint}/downtimes/${downtimeId}/DowntimePhase3AllInRate`,
      {}
    );
  }

  getByEvent(payload: any): Observable<Array<IDowntime>> {
    const params = new HttpParams().set("eventId", payload);
    return this.http.get<Array<IDowntime>>(
      `${environment.apiEndpoint}/downtimes`,
      {
        params,
        headers: new HttpHeaders({
          "Cache-control": "no-cache",
          Pragma: "no-cache",
          Expires: "0",
        }),
      },
    );
  }

  getWorklistItems(downtimeId: number) {
    return this.http.get<Array<any>>(
      `${environment.apiEndpoint}/downtimes/${downtimeId}/worklist-items`,
      {
        headers: {
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        },
      },
    );
  }

  //Response type updated to include salesforceSynced warning  and message details. Salesforce fail is now being treated as a warning instead of an error.
  applyJobTemplate(
    itemId: number,
    jobTemplateID: string,
    versionID: string,
    customName: string,
    ProductivityCalendar: number,
    AssignedScheduler: number,
    quickEstimateId?: number,
  ) {
    return this.http.post<ApplyJobTemplateResponse>(
      `${environment.apiEndpoint}/worklist-items/${itemId}/apply`,
      {
        jobTemplateID,
        versionID,
        customName,
        ProductivityCalendar,
        AssignedScheduler,
        quickEstimateId,
      },
    );
  }

  detachWorkListItem(itemId: number) {
    return this.http.post(
      `${environment.apiEndpoint}/worklist-items/${itemId}/disable`,
      null,
      {
        responseType: "text",
      },
    );
  }

  detachJobTemplate(itemId: number, appliedJobTemplateId: number) {
    return this.http.post(
      `${environment.apiEndpoint}/worklist-items/${itemId}/detach/${appliedJobTemplateId}`,
      null,
      {
        responseType: "text",
      },
    );
  }

  restoreJobTemplate(itemId: number, appliedJobTemplateId: number) {
    return this.http.post(
      `${environment.apiEndpoint}/worklist-items/${itemId}/restore/${appliedJobTemplateId}`,
      null,
      {
        responseType: "text",
      },
    );
  }

  getSalesforce(site: string): Observable<Array<ISalesForceDowntime>> {
    const params = new HttpParams().set("siteId", site);
    return this.http.get<Array<any>>(
      `${environment.apiEndpoint}/salesforce/downtimes`,
      { params },
    );
  }

  getSalesforceID(site: number, workListName: string) {
    return this.http.get(
      `${environment.apiEndpoint}/salesforce/downtimes/${site}/${workListName}/getSalesForceWorkListID`,
      {
        responseType: "text",
      },
    );
  }

  postSalesforce(
    downtime: string,
    eventId: number,
    siteId: number,
    title: string,
    refresh: boolean = false,
    salesforceDowntimeName?: string,
  ) {
     const encodedTitle = encodeURIComponent(title);
    let url = `${environment.apiEndpoint}/salesforce/downtimes/${downtime}/import?eventId=${eventId}&siteId=${siteId}&downtimeName=${encodedTitle}&refresh=${refresh}`;
    if (salesforceDowntimeName) {
      url += `&salesforceDowntimeName=${salesforceDowntimeName}`;
    }
    return this.http.post<Array<any>>(url, {
      eventId,
      siteId,
      downtimeName: title,
      downtimeTitle: downtime,
      refresh: refresh,
      salesforceDowntimeName: salesforceDowntimeName,
    });
  }

  getLatestWorkItemListCount(
    downtimeName: string,
    siteId: number,
  ): Observable<number> {
    const params = new HttpParams()
      .set("siteId", siteId)
      .set("downtimeName", downtimeName);

    return this.http.get<number>(
      `${environment.apiEndpoint}/salesforce/downtimes/CheckDtwlCount`,
      { params },
    );
  }

  // Downtime Discreet Items Methods
  // Normalization helper to handle PascalCase / camelCase responses
  private normalize(item: any): DowntimeDiscreetItemDTO {
    if (!item) {
      return {
        id: 0,
        downtimeId: 0,
        title: "",
        name: "",
        amount: 0,
        isDisabled: false,
      };
    }
    return {
      id: item.id ?? item.Id ?? item.ID ?? 0,
      downtimeId: item.downtimeId ?? item.DowntimeId ?? item.DowntimeID ?? 0,
      title: item.title ?? item.Title ?? item.Name ?? item.name ?? "",
      name: item.Name ?? item.name ?? item.title ?? item.Title ?? "",
      amount: item.amount ?? item.Amount ?? 0,
      isDisabled: item.isDisabled ?? item.IsDisabled ?? false,
      createdDate: item.createdDate ?? item.CreatedDate,
      modifiedDate: item.modifiedDate ?? item.ModifiedDate,
    };
  }

  getAllDiscreetItems(): Observable<DowntimeDiscreetItemDTO[]> {
    return this.http
      .get<any[]>(`${environment.apiEndpoint}/downtimeDiscreetItem`)
      .pipe(map((list) => (list || []).map((i) => this.normalize(i))));
  }

  getDiscreetItems(downtimeId: number): Observable<DowntimeDiscreetItemDTO[]> {
    const url = `${environment.apiEndpoint}/downtimeDiscreetItem/downtime/${downtimeId}`;
    return this.http
      .get<any[]>(url, {
        headers: new HttpHeaders({
          "Cache-Control": "no-cache",
          Pragma: "no-cache",
        }),
      })
      .pipe(
        map((list) => {
          console.log(
            "[DiscreetItems][SERVICE] GET list raw length:",
            Array.isArray(list) ? list.length : "non-array",
          );
          if (!Array.isArray(list)) {
            // If backend returns single object instead of array, wrap it
            list = list ? [list] : [];
          }
          return (list || []).map((i) => this.normalize(i));
        }),
      );
  }

  getDiscreetItemById(id: number): Observable<DowntimeDiscreetItemDTO> {
    return this.http
      .get<any>(`${environment.apiEndpoint}/downtimeDiscreetItem/${id}`)
      .pipe(map((i) => this.normalize(i)));
  }

  getDiscreetItemByTitle(
    downtimeId: number,
    title: string,
  ): Observable<DowntimeDiscreetItemDTO> {
    return this.http
      .get<any>(
        `${environment.apiEndpoint}/downtimeDiscreetItem/downtime/${downtimeId}/title/${title}`,
      )
      .pipe(map((i) => this.normalize(i)));
  }

  createDiscreetItem(
    item: DowntimeDiscreetItemDTO,
  ): Observable<DowntimeDiscreetItemDTO> {
    const payload = {
      Title: item.title ?? item.name ?? "",
      Amount: item.amount ?? 0,
      IsDisabled: item.isDisabled ?? false,
      DowntimeID: item.downtimeId ?? 0,
    };
    console.log(
      "Creating new discreet item with payload:",
      JSON.stringify(payload, null, 2),
    );
    return this.http
      .post<any>(`${environment.apiEndpoint}/downtimeDiscreetItem`, payload)
      .pipe(
        map((i) => {
          console.log("Create response:", i);
          return this.normalize(i);
        }),
        catchError((error) => {
          console.error("Error creating discreet item:", error);
          return throwError(() => error);
        }),
      );
  }

  updateDiscreetItem(
    id: number,
    item: DowntimeDiscreetItemDTO,
  ): Observable<DowntimeDiscreetItemDTO> {
    const payload = {
      ID: id,
      Title: item.title ?? item.name ?? "",
      Amount: item.amount ?? 0,
      IsDisabled: item.isDisabled ?? false,
      DowntimeID: item.downtimeId ?? 0,
    };
    console.log(
      "Updating discreet item with ID:",
      id,
      "Payload:",
      JSON.stringify(payload, null, 2),
    );
    return this.http
      .put<any>(
        `${environment.apiEndpoint}/downtimeDiscreetItem/${id}`,
        payload,
      )
      .pipe(
        map((i) => {
          console.log("Update response:", i);
          return this.normalize(i);
        }),
        catchError((error) => {
          console.error("Error updating discreet item:", error);
          return throwError(() => error);
        }),
      );
  }

  toggleDiscreetItemFlag(
    id: number,
    disabled: boolean,
  ): Observable<DowntimeDiscreetItemDTO> {
    const url = `${environment.apiEndpoint}/downtimeDiscreetItem/${id}/toggle-disable`;
    return this.http
      .put<any>(url, {}, { params: { disabled: disabled.toString() } })
      .pipe(
        map((i) => this.normalize(i)),
        catchError((error) => {
          console.error("Error toggling discreet item flag:", error);
          return throwError(() => error);
        }),
      );
  }

  deleteDiscreetItem(id: number): Observable<DowntimeDiscreetItemDTO> {
    // If true deletion is later added, replace with DELETE call
    return this.toggleDiscreetItemFlag(id, true);
  }

  saveDiscreetItem(
    item: DowntimeDiscreetItemDTO,
  ): Observable<DowntimeDiscreetItemDTO> {
    return item.id && item.id > 0
      ? this.updateDiscreetItem(item.id, item)
      : this.createDiscreetItem(item);
  }

  saveAllDiscreetItems(
    items: DowntimeDiscreetItemDTO[],
  ): Observable<DowntimeDiscreetItemDTO[]> {
    if (!items || items.length === 0) {
      return of([]);
    }
    const saveRequests = items.map((item) =>
      this.saveDiscreetItem(item).pipe(
        catchError((error) => {
          console.error("Error saving individual item:", item, error);
          return throwError(() => error);
        }),
      ),
    );
    return forkJoin(saveRequests).pipe(
      map((list) => list.map((i) => this.normalize(i))),
      catchError((error) => {
        console.error("Error in forkJoin saveAllDiscreetItems:", error);
        return throwError(() => error);
      }),
    );
  }

  getDowntimeSummary(downtimeID: number): Observable<Downtime> {
    return this.http
      .get<Downtime>(
        `${environment.apiEndpoint}/downtimes/${downtimeID}/total-labor-cost-estimate`,
        {
          headers: {
            "Cache-Control": "no-cache",
            Pragma: "no-cache",
          },
        },
      )
      .pipe(
        catchError((error) => {
          console.warn(
            "Downtime summary endpoint not available:",
            error.status,
          );
          // Return empty/default downtime object if endpoint not found
          if (error.status === 404) {
            return of({} as Downtime);
          }
          return throwError(() => error);
        }),
      );
  }
}

export interface ISalesForceDowntime {
  DowntimeTitle: string;
  IM_Downtime_Id__c: string;
  IM_Downtime_Title__c: string;
  Name: string;
  id: string;
}

export interface DowntimeDiscreetItemDTO {
  id?: number;
  downtimeId: number;
  title: string;
  name?: string;
  amount: number;
  amountDisplay?: string;
  isDisabled?: boolean;
  createdDate?: Date;
  modifiedDate?: Date;
}
